/* eslint-disable @typescript-eslint/no-explicit-any */
import type { HTMLAttributes } from 'react';
import type { ManageMetricsType } from '../../../forms/types';
import type { AdditionalHeaderCellProps, CustomColumnProps, CustomRowProps } from '../../../components/table/types';
import type { ClassReportStudentType, ClassReportType, StudentDataType, StudentStatusGroupType } from '../../types';
import React, { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { DateTime } from 'luxon';
import { TickStatusEnum } from '@whizz/api-model';
import styled, { createGlobalStyle } from 'styled-components';
import { GreenTickStatus, StudentStatus, TableHeader } from './ReportTable.components';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Button, MaterialIcon, Table } from '../../../components';
import { BUTTON_ICON_WRAPPER, TABLE_SORT } from '../../../util/data-id';
import { GLOBAL_ICONS } from '../../../util/vars';
import HighlightCard from '../modules/HighlightCard';
import { greenTickSort, studentStatusSort } from '../../../helper/customColumnsSortBy';
import * as d3 from 'd3';
import classNames from 'classnames';

export type ClassTableAccessors =
  | 'id'
  | 'name'
  | 'maths_age'
  | 'maths_age_improvements'
  | 'progressions'
  | 'quality'
  | 'consistency'
  | 'green_tick'
  | 'student_status'
  | 'last_assessment'
  | 'time_span'
  | 'maths_age_at_start'
  | 'tutor_usage'
  | 'topic_challenge_usage'
  | 'topic_challenges_passed'
  | 'progression_pass_rate'
  | 'exercise_mark_avg'
  | 'test_mark_avg'
  | 'age'
  | 'minutes'
  | 'parent'
  | 'highlighted';

type MetricType = keyof ClassReportStudentType;
type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;

interface Props extends HTMLProps {
  /**
   * Class report data.
   */
  classReport: ClassReportType;
  /**
   * Highlighted element(s) id.
   */
  filterLabel: string | null;
  highlightedIds: number[] | null;
  showOthersData: boolean;
  metricsSettings: ManageMetricsType;
  onStudentClick?: (id: number) => void;
}

const COLUMN_WIDTHS = {
  'name': 200,
};

const TABLE_ID = 'class_report';

const GlobalStyle = createGlobalStyle<{ columns: number }>`
  table[id="${TABLE_ID}"] {
    thead {
      tr:nth-child(2) {
        border: 1px solid black;
        border-right: 0;
        border-left: 0;
        font-weight: bold;
      }
    },
    [data-id='footer_maths_age'],
    [data-id='footer_maths_age_improvements'],
    [data-id='footer_progressions'],
    [data-id='footer_quality'],
    [data-id='footer_consistency'],
    [data-id='footer_green_tick'],
    [data-id='footer_student_status'],
    [data-id='footer_last_assessment'],
    [data-id='footer_time_span'],
    [data-id='footer_maths_age_at_start'],
    [data-id='footer_tutor_usage'],
    [data-id='footer_topic_challenge_usage'],
    [data-id='footer_topic_challenges_passed'],
    [data-id='footer_progression_pass_rate'],
    [data-id='footer_exercise_mark_avg'],
    [data-id='footer_test_mark_avg'],
    [data-id='footer_age'],
    [data-id='footer_minutes'],
    [data-id='footer_parent'],
    [data-column='cell_maths_age'],
    [data-column='cell_maths_age_improvements'],
    [data-column='cell_progressions'],
    [data-column='cell_quality'],
    [data-column='cell_consistency'],
    [data-column='cell_green_tick'],
    [data-column='cell_student_status'],
    [data-column='cell_last_assessment'],
    [data-column='cell_time_span'],
    [data-column='cell_maths_age_at_start'],
    [data-column='cell_tutor_usage'],
    [data-column='cell_topic_challenge_usage'],
    [data-column='cell_topic_challenges_passed'],
    [data-column='cell_progression_pass_rate'],
    [data-column='cell_exercise_mark_avg'],
    [data-column='cell_test_mark_avg'],
    [data-column='cell_age'],
    [data-column='cell_minutes'],
    [data-column='cell_parent']
    {
      text-align: center;
      > div {
        text-align: center;
        width: 100%;
        white-space: nowrap;
      }
    },
    [data-column='cell_name'] {
      font-weight: normal !important;
    },
    [data-id='col_name'],
    [data-id='col_maths_age'],
    [data-id='col_maths_age_improvements'],
    [data-id='col_progressions'],
    [data-id='col_quality'],
    [data-id='col_consistency'],
    [data-id='col_green_tick'],
    [data-id='col_student_status'],
    [data-id='col_last_assessment'],
    [data-id='col_time_span'],
    [data-id='col_maths_age_at_start'],
    [data-id='col_tutor_usage'],
    [data-id='col_topic_challenge_usage'],
    [data-id='col_topic_challenges_passed'],
    [data-id='col_progression_pass_rate'],
    [data-id='col_exercise_mark_avg'],
    [data-id='col_test_mark_avg'],
    [data-id='col_age'],
    [data-id='col_minutes'],
    [data-id='col_parent']
    {
      > div {
        height: 100%;
        flex-direction: column;
        display: grid;
        grid-template-rows: auto 30px;
        > div {
          grid-column-start: span 2;
        }
        [data-id='${BUTTON_ICON_WRAPPER}'] {
          grid-column-start: 1;
          justify-self: end;
          align-self: end;
        }
        [data-id='${TABLE_SORT}'] {
          grid-column-start: 2;
          justify-self: start;
          align-self: end;
        }
      }
    }
    [data-id^='col'] {
      width: 100%;
      @supports (-moz-appearance:none) {
        height: 120px !important;
      }
    }
    @media (min-width: 768px) {
      [data-id^='col'] {
        width: calc(100% / ${(props) => props.columns});
      }
    }
    [data-id='col_name'] {
      min-width: ${COLUMN_WIDTHS.name}px;
    }
  }
`;

const getStudentsWithMathsAge = (students: ClassReportStudentType[], highlightedIds: number[]) => {
  return students.filter((student) => highlightedIds.includes(student.id) && student.chartsData.mathsAge > 0);
};

const getGreenTickStatusPercentage = (students: ClassReportStudentType[]) => {
  const greenTickCount = students.filter((student) => student.tickStatus === TickStatusEnum.GREEN).length;
  return ((greenTickCount / students.length) * 100).toFixed(0);
};

const getTopicChallengesPassed = (students: ClassReportStudentType[]) => {
  const { count, total } = students.reduce((prev, value) => {
    return {
      count: prev.count + value.topicChallengesPassed.count,
      total: prev.total + value.topicChallengesPassed.total,
    };
  }, { count: 0, total: 0 });
  const averageCount = (count / students.length).toFixed(0);
  const averageTotal = (total / students.length).toFixed(0);
  return `${averageCount}/${averageTotal}`;
};

const getAverage = (tutorUsageAverage: number, progressionAverage: number) => {
  if (tutorUsageAverage <= 0 || progressionAverage <= 0) {
    return '-';
  }
  return (tutorUsageAverage / progressionAverage).toFixed(1);
};

const ReportTable = ({ classReport, highlightedIds, filterLabel, showOthersData, metricsSettings, onStudentClick }: Props): JSX.Element => {
  const { locale, translate } = useI18n();

  const showClassAverage = showOthersData;
  const showFilteredAverage = highlightedIds ? highlightedIds.length > 0 : false;

  const classAverageLabel = (label: string | null) => showClassAverage && label ? label : null;
  const filteredAverageLabel = (label: string | null) => showFilteredAverage && label ? label : null;

  const getColumnAverage = (metric: MetricType | StudentDataType, fixed = 1) => {
    if (highlightedIds && highlightedIds?.length > 0) {
      const studentListWithZeroMathsAgeRemoved = getStudentsWithMathsAge(classReport.students.all, highlightedIds);
      const mean = d3.mean(studentListWithZeroMathsAgeRemoved.map((student) => {
        if (metric in student) {
          return Number(student[metric as MetricType]);
        } else if (metric in student.chartsData) {
          return Number(student.chartsData[metric as StudentDataType]);
        }
      }));
      if (mean !== undefined) {
        return mean.toFixed(fixed);
      }
    }
    return null;
  };

  const columns = [
    {
      id: 'name',
      Header: <TableHeader
        title={''}
      />,
      Cell: ({ row }: { row: any }) => {
        const label = row.original.highlighted ?
          (<div className={'flex my-sm'}>
            <HighlightCard label={row.values.name} highlight margin={false} />
          </div>)
          : row.values.name;
        return onStudentClick ? (<button className={'underline hover:no-underline'} onClick={() => onStudentClick(parseInt(row.original.id))}>{label}</button>) : label;
      }
      ,
      accessor: 'name',
      Footer: <FooterComponent highlight classAverage={classAverageLabel(translate('pages.REPORTS.HEADERS.CLASS_AVERAGE').toString())} filteredAverage={filteredAverageLabel(translate('pages.REPORTS.HEADERS.FILTERED_AVERAGE', { filtered: filterLabel } ).toString())} />,
      sticky: 'left',
    },
    {
      id: 'last_assessment',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.LAST_ASSESSMENT.label')}
      />,
      Cell: ({ row }: { row: any }) => {
        let last_assessment = row.original.last_assessment;
        if (!last_assessment) {
          return '';
        }
        if (!DateTime.isDateTime(last_assessment)) {
          last_assessment = DateTime.fromISO(last_assessment);
        }
        return last_assessment.setLocale(locale ? locale.id : 'en-gb').toLocaleString();
      },
      tooltipMessage: translate('pages.REPORTS.HEADERS.LAST_ASSESSMENT.info'),
      accessor: 'last_assessment',
      Footer: '',
    },
    {
      id: 'age',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.AGE.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.AGE.info'),
      accessor: 'age',
      Footer: <FooterComponent classAverage={classAverageLabel(classReport.actualAgeAverage.toFixed(2))} filteredAverage={filteredAverageLabel(getColumnAverage('actualAge', 2))} />,
    },
    {
      id: 'maths_age_at_start',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.MATHS_AGE_AT_START.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.MATHS_AGE_AT_START.info'),
      accessor: 'maths_age_at_start',
      Footer: <FooterComponent classAverage={classAverageLabel(classReport.mathsAgeAtStartAverage.toFixed(2))} filteredAverage={filteredAverageLabel(getColumnAverage('mathsAgeAtStart', 2))} />,
    },
    {
      id: 'maths_age',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.MATHS_AGE.label')}
        subtitle={translate('pages.REPORTS.HEADERS.SUBTITLES.YEARS')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.MATHS_AGE.info'),
      accessor: 'maths_age',
      Footer: <FooterComponent classAverage={classAverageLabel(classReport.mathsAgeAverage.toFixed(2))} filteredAverage={filteredAverageLabel(getColumnAverage('mathsAge', 2))} />,
    },
    {
      id: 'maths_age_improvements',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.IMPROVEMENT.label')}
        subtitle={translate('pages.REPORTS.HEADERS.SUBTITLES.WEEKS')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.IMPROVEMENT.info'),
      accessor: 'maths_age_improvements',
      Footer: <FooterComponent classAverage={classAverageLabel(classReport.mathsAgeImprovementAverage.toFixed(1))} filteredAverage={filteredAverageLabel(getColumnAverage('mathsAgeImprovement', 1))} />,
      sortType: (a, b, id) => Number(a.values[id]) - Number(b.values[id]),
    },
    {
      id: 'time_span',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.TIME_SPAN.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.TIME_SPAN.info'),
      accessor: 'time_span',
      Footer: <FooterComponent classAverage={classAverageLabel(classReport.timeSpanAverage.toFixed(2))} filteredAverage={filteredAverageLabel(getColumnAverage('timeSpan', 2))} />,
    },
    {
      id: 'progressions',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.PROGRESSIONS.label')}
        subtitle={translate('pages.REPORTS.HEADERS.SUBTITLES.WEEK')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.PROGRESSIONS.info'),
      accessor: 'progressions',
      Footer: <FooterComponent classAverage={classAverageLabel(classReport.progressionAverage.toFixed(1))} filteredAverage={filteredAverageLabel(getColumnAverage('progression', 1))} />,
    },
    {
      id: 'quality',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.QUALITY_LEARNING_TIME.label')}
        subtitle={translate('pages.REPORTS.HEADERS.SUBTITLES.MINS_PER_WEEK')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.QUALITY_LEARNING_TIME.info'),
      accessor: 'quality',
      Footer: <FooterComponent classAverage={classAverageLabel(classReport.qualityLearningTimeAverage.toFixed(0))} filteredAverage={filteredAverageLabel(getColumnAverage('qualityLearningTime', 0))} />,
    },
    {
      id: 'tutor_usage',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.TUTOR_USAGE.label')}
        subtitle={translate('pages.REPORTS.HEADERS.SUBTITLES.MINS_PER_WEEK')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.TUTOR_USAGE.info'),
      accessor: 'tutor_usage',
      Footer: <FooterComponent classAverage={classAverageLabel(classReport.tutorUsageAverage.toFixed(0))} filteredAverage={filteredAverageLabel(getColumnAverage('tutorUsage', 0))} />,
    },
    {
      id: 'topic_challenge_usage',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.TOPIC_CHALLENGE_USAGE.label')}
        subtitle={translate('pages.REPORTS.HEADERS.SUBTITLES.MINS_PER_WEEK')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.TOPIC_CHALLENGE_USAGE.info'),
      accessor: 'topic_challenge_usage',
      Footer: <FooterComponent classAverage={classAverageLabel(classReport.topicChallengeUsageAverage.toFixed(0))} filteredAverage={filteredAverageLabel(getColumnAverage('topicChallengeUsage', 0))} />,
    },
    {
      id: 'topic_challenges_passed',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.TOPIC_CHALLENGES_PASSED.label')}
      />,
      Cell: ({ row }: { row: any }) => {
        const { count, total } = row.values.topic_challenges_passed;
        return `${count}/${total}`;
      },
      tooltipMessage: translate('pages.REPORTS.HEADERS.TOPIC_CHALLENGES_PASSED.info'),
      accessor: 'topic_challenges_passed',
      Footer: (
        <FooterComponent
          classAverage={classAverageLabel(getTopicChallengesPassed(classReport.students.all))}
          filteredAverage={filteredAverageLabel(getTopicChallengesPassed(getStudentsWithMathsAge(classReport.students.all, highlightedIds ?? [])))}
        />),
      sortType: (a, b) => {
        const aCount = Number(a.values.topic_challenges_passed.count);
        const bCount = Number(b.values.topic_challenges_passed.count);
        const aTotal = Number(a.values.topic_challenges_passed.total);
        const bTotal = Number(b.values.topic_challenges_passed.total);
        return aCount - bCount || aTotal - bTotal;
      },
    },
    {
      id: 'minutes',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.MINUTES.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.MINUTES.info'),
      accessor: 'minutes',
      Footer: <FooterComponent classAverage={classAverageLabel(getAverage(classReport.tutorUsageAverage, classReport.progressionAverage))} filteredAverage={filteredAverageLabel(getAverage(Number(getColumnAverage('tutorUsage', 2)), Number(getColumnAverage('progression'))))} />,
    },
    {
      id: 'green_tick',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.GREEN_TICK_STATUS.label')}
      />,
      Cell: ({ row }: { row: any }) =>
        <GreenTickStatus
          status={row.original.green_tick}
        />
      ,
      tooltipMessage: translate('pages.REPORTS.HEADERS.GREEN_TICK_STATUS.info'),
      accessor: 'green_tick',
      Footer: (
        <FooterComponent
          classAverage={classAverageLabel(translate('pages.REPORTS.HEADERS.GREEN_TICK_STATUS.value', { percentage: getGreenTickStatusPercentage(classReport.students.all) }).toString())}
          filteredAverage={filteredAverageLabel(translate('pages.REPORTS.HEADERS.GREEN_TICK_STATUS.value', { percentage: getGreenTickStatusPercentage(getStudentsWithMathsAge(classReport.students.all, highlightedIds ?? [])) }).toString())}
        />),
      sortType: useMemo(() => greenTickSort, []),
    },
    {
      id: 'student_status',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.STUDENT_STATUS.label')}
      />,
      Cell: ({ row }: { row: any }) =>
        <StudentStatus
          status={row.original.student_status as StudentStatusGroupType}
        />
      ,
      tooltipMessage: translate('pages.REPORTS.HEADERS.STUDENT_STATUS.info'),
      accessor: 'student_status',
      Footer: '',
      sortType: useMemo(() => studentStatusSort, []),
    },
    {
      id: 'progression_pass_rate',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.PROGRESSION_PASS_RATE.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.PROGRESSION_PASS_RATE.info'),
      accessor: 'progression_pass_rate',
      Footer: <FooterComponent classAverage={classAverageLabel(classReport.progressionPassRateAverage.toFixed(0))} filteredAverage={getColumnAverage('progressionPassRate', 0)} />,
    },
    {
      id: 'exercise_mark_avg',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.EXERCISE_MARK_AVG.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.EXERCISE_MARK_AVG.info'),
      accessor: 'exercise_mark_avg',
      Footer: <FooterComponent classAverage={classAverageLabel(classReport.exerciseMarkAverage.toFixed(0))} filteredAverage={getColumnAverage('exerciseMark', 0)} />,
    },
    {
      id: 'test_mark_avg',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.TEST_MARK_AVG.label')}
      />,
      tooltipMessage: translate('pages.REPORTS.HEADERS.TEST_MARK_AVG.info'),
      accessor: 'test_mark_avg',
      Footer: <FooterComponent classAverage={classAverageLabel(classReport.testMarkAverage.toFixed(0))} filteredAverage={getColumnAverage('testMark', 0)} />,
    },
    {
      id: 'parent',
      Header: <TableHeader
        title={translate('pages.REPORTS.HEADERS.PARENT.label')}
      />,
      Cell: ({ row }: { row: any }) =>
        row.original.parent
          ?
          <div className={'flex justify-center'}>
            <MaterialIcon
              icon={GLOBAL_ICONS.parent_link_tick}
              color={'primary'}
              title={'tick'}
            />
          </div>
          : ''
      ,
      tooltipMessage: translate('pages.REPORTS.HEADERS.PARENT.info'),
      accessor: 'parent',
      Footer: '',
    },
  ] as (CustomColumnProps<ClassTableAccessors> & AdditionalHeaderCellProps)[];

  const filteredColumns = columns.filter((c) => 'name' === c.id
    || metricsSettings.default_settings.includes(c.id!)
    || metricsSettings.additional_settings.includes(c.id!),
  ) as (CustomColumnProps<ClassTableAccessors> & AdditionalHeaderCellProps)[];

  const getFormattedData = (): CustomRowProps<ClassTableAccessors>[] => {
    return classReport.students.all
      .map((student) => {
        return {
          id: student.id.toString(),
          name: student.fullName,
          maths_age: student.chartsData.mathsAge.toFixed(2),
          maths_age_improvements: student.chartsData.mathsAgeImprovement.toFixed(1),
          progressions: student.chartsData.progression,
          quality: student.chartsData.qualityLearningTime,
          consistency: student.consistency,
          green_tick: student.tickStatus,
          student_status: student.studentStatus,
          last_assessment: student.lastAssessmentDate,
          time_span: student.timeSpan,
          maths_age_at_start: student.mathsAgeAtStart.toFixed(2),
          tutor_usage: student.tutorUsage,
          topic_challenge_usage: student.topicChallengeUsage,
          topic_challenges_passed: student.topicChallengesPassed,
          progression_pass_rate: student.progressionPassRate,
          exercise_mark_avg: student.exerciseMark,
          test_mark_avg: student.testMark,
          age: student.actualAge,
          minutes: getAverage(student.tutorUsage, student.chartsData.progression),
          parent: student.parentLink,
          highlighted: highlightedIds?.includes(student.id),
        };
      })
    ;
  };

  const [ studentData, setStudentData ] = useState(getFormattedData());

  useEffect(() => {
    setStudentData(getFormattedData());
  }, [ highlightedIds ]);

  const getStyle = (index: number): Record<string, unknown> => {
    if (studentData[index].highlighted) {
      return {
        style: {
          'border': '1px solid black',
          'border-right': '0px',
          'border-left': '0',
          'font-weight': 'bold',
        },
      };
    } else if (!showOthersData) {
      return {
        style: {
          'display': 'none',
        },
      };
    } else {
      return {};
    }
  };

  return (
    <StyledWrapper className={'container bg-white rounded-default shadow'}>
      <GlobalStyle columns={filteredColumns.length}/>
      <Table
        id={TABLE_ID}
        cols={filteredColumns}
        content={studentData}
        sticky
        footerPosition={showOthersData ? 'top' : null}
        getRowProps={(row) => (
          getStyle(row.index)
        )}
        initialState={{
          sortBy: [
            {
              id: 'name',
              desc: false,
            },
          ],
        }}
      />
    </StyledWrapper>
  );
};

const StyledWrapper = styled('div')`
  max-width: initial;
  width: fit-content;
`;

const FooterComponent = ({ classAverage, filteredAverage, highlight = false }: { classAverage: string | number | null, filteredAverage?: string | number | null, highlight?: boolean }) => {
  return (
    <div className={`font-bold my-sm ${classNames({ 'grid grid-rows-2 h-full items-center gap-2' : classAverage && filteredAverage })}`}>
      { classAverage ? <div>{classAverage}</div> : null }
      { filteredAverage ? (highlight ? <HighlightCard label={filteredAverage?.toString()} highlight margin={false} /> : <div>{filteredAverage}</div> ) : null }
    </div>
  );
};

ReportTable.displayName = 'ReportTable';

export default ReportTable;
